import React, { useState, useMemo, useCallback } from 'react';
import { Sparkles, ArrowRight, RotateCcw } from 'lucide-react';

/**
 * Plan Recommender Wizard
 *
 * Asks the visitor a sequence of Yes/No questions and tallies points toward
 * the plan(s) each question's "Yes" answer favours. When all questions are
 * answered, the highest-scoring plan id is emitted via `onRecommendation`
 * so the parent can highlight that column / card in the comparison view.
 */
const PlanRecommenderWizard = ({
  title,
  subtitle,
  questions,
  plans,
  accentColor,
  onRecommendation,
  recommendedPlanId,
}) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const total = (questions || []).length;
  const current = (questions || [])[step] || null;
  const done = step >= total && total > 0;

  const recommendedPlan = useMemo(() => {
    if (!recommendedPlanId) return null;
    return (plans || []).find((p) => p.id === recommendedPlanId) || null;
  }, [plans, recommendedPlanId]);

  const handleAnswer = useCallback((q, value) => {
    const nextAnswers = { ...answers, [q.id]: value };
    setAnswers(nextAnswers);
    const nextStep = step + 1;
    setStep(nextStep);
    if (nextStep >= total) {
      // Tally
      const tally = {};
      (plans || []).forEach((p) => { tally[p.id] = 0; });
      (questions || []).forEach((qq) => {
        if (nextAnswers[qq.id] === 'yes') {
          (qq.yesPlanIds || []).forEach((pid) => {
            tally[pid] = (tally[pid] || 0) + 1;
          });
        }
      });
      let winnerId = null;
      let winnerScore = -1;
      Object.entries(tally).forEach(([pid, score]) => {
        if (score > winnerScore) {
          winnerId = pid;
          winnerScore = score;
        }
      });
      // Fallback to a plan flagged `highlighted` if no Yes answers gave points
      if (winnerScore <= 0) {
        const fallback = (plans || []).find((p) => p.highlighted) || (plans || [])[0];
        winnerId = fallback ? fallback.id : null;
      }
      if (onRecommendation && winnerId) onRecommendation(winnerId);
    }
  }, [answers, step, total, questions, plans, onRecommendation]);

  const reset = () => {
    setAnswers({});
    setStep(0);
    if (onRecommendation) onRecommendation(null);
  };

  if (total === 0) return null;

  const progress = Math.round((Math.min(step, total) / total) * 100);

  return (
    <div
      className="rounded-2xl border bg-white p-6 md:p-8 mb-10"
      style={{ borderColor: `${accentColor}33`, boxShadow: `0 1px 3px ${accentColor}1A` }}
      data-testid="plan-recommender-wizard"
    >
      <div className="flex items-start gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${accentColor}1A`, color: accentColor }}
        >
          <Sparkles size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold" style={{ color: 'var(--site-heading-color)' }}>
            {title || 'Find the right plan for you'}
          </h3>
          {subtitle && (
            <p className="text-sm opacity-70 mt-0.5" style={{ color: 'var(--site-body-color)' }}>
              {subtitle}
            </p>
          )}
        </div>
        {(step > 0 || done) && (
          <button
            type="button"
            onClick={reset}
            className="text-xs flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-100 transition-colors"
            style={{ color: accentColor }}
            data-testid="recommender-reset-btn"
          >
            <RotateCcw size={12} /> Reset
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden mb-5">
        <div
          className="h-full transition-all duration-500 ease-out"
          style={{
            width: `${progress}%`,
            backgroundColor: accentColor,
          }}
        />
      </div>

      {!done && current && (
        <div>
          <p className="text-xs uppercase tracking-wider opacity-60 mb-2">Question {step + 1} of {total}</p>
          <p className="text-base md:text-lg font-medium mb-4" style={{ color: 'var(--site-heading-color)' }}>
            {current.text}
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => handleAnswer(current, 'yes')}
              className="flex-1 min-w-[120px] px-5 py-3 rounded-lg font-medium text-white transition-transform hover:scale-[1.02]"
              style={{ backgroundColor: accentColor }}
              data-testid={`recommender-yes-${current.id}`}
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => handleAnswer(current, 'no')}
              className="flex-1 min-w-[120px] px-5 py-3 rounded-lg font-medium border-2 transition-colors hover:bg-slate-50"
              style={{ borderColor: '#cbd5e1', color: 'var(--site-body-color)' }}
              data-testid={`recommender-no-${current.id}`}
            >
              No
            </button>
          </div>
        </div>
      )}

      {done && recommendedPlan && (
        <div className="text-center py-2" data-testid="recommender-result">
          <p className="text-xs uppercase tracking-wider opacity-60 mb-2">Our recommendation</p>
          <p className="text-2xl md:text-3xl font-bold mb-3" style={{ color: accentColor }}>
            {recommendedPlan.name}
          </p>
          {recommendedPlan.buttonText && recommendedPlan.buttonUrl && (
            <a
              href={recommendedPlan.buttonUrl}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-white transition-transform hover:scale-[1.02]"
              style={{ backgroundColor: accentColor }}
            >
              {recommendedPlan.buttonText} <ArrowRight size={16} />
            </a>
          )}
          <p className="text-xs opacity-60 mt-3">See the highlighted column below for full details.</p>
        </div>
      )}
    </div>
  );
};

export default PlanRecommenderWizard;
